import { useEffect, useState } from 'react';
import Text from '@deprecated-ui/common/Text';
import Flex from '@deprecated-ui/common/Flex';
import If from 'global/ErrorHandlers/If';
import DayTradeBanner from 'features/day-trade/components/Banner';
import { TransactionHistoryListItem } from 'global/Securities/types/api/history';
import Icon from '@ant-design/icons';

import Collapsible from 'global/Collapsible';
import InfoSvg from 'global/Securities/components/Icons/InfoSvg';
import { BorderedContainer } from 'global/TradingModal/RealTradingModal/ModalBonds/styled';
import { BondInfoType } from 'global/TradingModal/RealTradingModal/ModalBonds/constants';
import { useBondInfoModal } from 'global/TradingModal/RealTradingModal/ModalBonds/stores/useBondInfoModal';
import DetailModal from '../DetailModal';
import { iModal } from '../types';

import { getDetailTableData } from './DetailTableData';
import HistoryFeeModal from './HistoryFeeModal';
import { REDEMPTIONS, FR_BONDS, HistoryType } from './types';
import { HistoryModalInfoTable } from './styled';
import { formatCeilToDecimals } from './utils';
import PnLCurrency from '../Portfolio/PnLCurrency';

interface HistoryModalProps extends iModal {
  record: TransactionHistoryListItem;
  onCompanyClick?(): void;
}

enum HistoryModalTypeProps {
  DETAIL = 'DETAIL',
  FEE = 'FEE',
}

export const HistoryModal = ({
  record,
  onCancel,
  onCompanyClick,
  visible,
}: HistoryModalProps) => {
  const openInfoModal = useBondInfoModal((s) => s.openInfoModal);
  const [currentRecord, setCurrentRecord] = useState(null);

  const [modalType, setModalType] = useState<HistoryModalTypeProps>(
    HistoryModalTypeProps.DETAIL,
  );

  const handleOpenFeeModal = () => {
    setModalType(HistoryModalTypeProps.FEE);
  };

  useEffect(() => {
    if (record?.display_as) {
      const dataSource = getDetailTableData(
        record,
        handleOpenFeeModal,
        openInfoModal,
      );
      setCurrentRecord(dataSource);
    }
  }, [record]);

  const onCancelFull = () => {
    setCurrentRecord(null);
    onCancel();
  };

  const isShowForcellBadge =
    record?.is_day_trade &&
    record?.command === HistoryType.FORCED_SELL_DAY_TRADE;

  return (
    <>
      <DetailModal
        visible={
          visible &&
          Boolean(record) &&
          modalType === HistoryModalTypeProps.DETAIL
        }
        onCancel={onCancelFull}
        title={record?.display_as}
        symbol={record?.symbol}
        showPrice={false}
        isBonds={FR_BONDS.includes(record?.command)}
        isRedemption={REDEMPTIONS.includes(record?.command)}
        bondsName={record?.bonds?.product_name}
        onCompanyClick={onCompanyClick}
      >
        <If condition={isShowForcellBadge}>
          <DayTradeBanner
            label="FORCED SELL"
            timeOpen={`${record?.date} ${record?.time}`}
            forcedSell
          />
        </If>
        <HistoryModalInfoTable dataSource={currentRecord} />
        <If condition={record?.command === HistoryType.LATE_FEE}>
          <HistoryModalInfoTable
            dataSource={[
              {
                label: <Text as="span">Date</Text>,
                value: (
                  <Text as="div" align="right">
                    {record?.date}
                  </Text>
                ),
              },
              {
                label: (
                  <Text as="span" family="bold">
                    Late Fee
                  </Text>
                ),
                value: (
                  <Text as="div" align="right" family="bold">
                    {formatCeilToDecimals(record?.netamount, '-')}
                  </Text>
                ),
              },
            ]}
          />
        </If>
        <If condition={record?.command === HistoryType.SELL_FR}>
          <BorderedContainer>
            <Collapsible
              header={
                <Flex align="center" justify="space-between">
                  <Flex direction="column" align="start">
                    {/* @ts-expect-error */}
                    <Text data-cy="bond-total-pl-label" mr="4px">
                      P&L (Nett)
                    </Text>
                  </Flex>

                  <PnLCurrency
                    dataCy="bond-total-pl-content"
                    amount={record?.realized_amount}
                    percentage={record?.realized_percentage}
                    isMultiline
                    family="bold"
                  />
                </Flex>
              }
            >
              {/* @ts-expect-error */}
              <Flex align="center" justify="space-between" py="8px">
                <Flex align="center">
                  {/* @ts-expect-error */}
                  <Text data-cy="bond-accrued-interest-label" mr="4px">
                    Kupon Berjalan (Nett)
                  </Text>
                  <Icon
                    component={InfoSvg}
                    style={{ color: '#969696' }}
                    onClick={() => openInfoModal(BondInfoType.ACCRUED_INTEREST)}
                  />
                </Flex>
                <PnLCurrency
                  dataCy="bond-accrued-interest-content"
                  amount={record?.bonds?.accrued_interest_nett}
                />
              </Flex>
              {/* @ts-expect-error */}
              <Flex align="center" justify="space-between" py="8px">
                <Flex align="center">
                  {/* @ts-expect-error */}
                  <Text data-cy="bond-capital-gain-loss-label" mr="4px">
                    Capital Gain / Loss (Nett)
                  </Text>
                </Flex>
                <PnLCurrency
                  dataCy="bond-capital-gain-loss-content"
                  amount={record?.bonds?.capital_gain_loss?.amount_nett}
                />
              </Flex>
            </Collapsible>
          </BorderedContainer>

          <BorderedContainer>
            <Collapsible
              header={
                <Flex align="center" justify="space-between">
                  <Flex direction="column" align="start">
                    <Flex align="center">
                      {/* @ts-expect-error */}
                      <Text data-cy="bond-total-pl-label" mr="4px">
                        Total Keuntungan (Nett)
                      </Text>
                    </Flex>
                    <Text size="12px" color="#B5B5B5">
                      Selama Investasi
                    </Text>
                  </Flex>

                  <PnLCurrency
                    dataCy="bond-total-pl-content"
                    amount={record?.bonds.total_realized_amount}
                    percentage={record?.bonds.total_realized_amount_percentage}
                    isMultiline
                    family="bold"
                  />
                </Flex>
              }
            >
              {/* @ts-expect-error */}
              <Flex align="center" justify="space-between" py="8px">
                <Flex align="center">
                  {/* @ts-expect-error */}
                  <Text data-cy="bond-accrued-interest-label" mr="4px">
                    Total Kupon Harian (Nett)
                  </Text>
                  <Icon
                    component={InfoSvg}
                    style={{ color: '#969696' }}
                    onClick={() =>
                      openInfoModal(BondInfoType.TOTAL_DAILY_COUPON)
                    }
                  />
                </Flex>
                <PnLCurrency
                  dataCy="bond-accrued-interest-content"
                  amount={record?.bonds.daily_accrued_interest}
                />
              </Flex>
              {/* @ts-expect-error */}
              <Flex align="center" justify="space-between" py="8px">
                <Flex align="center">
                  {/* @ts-expect-error */}
                  <Text data-cy="bond-capital-gain-loss-label" mr="4px">
                    Capital Gain / Loss (Nett)
                  </Text>
                </Flex>
                <PnLCurrency
                  dataCy="bond-capital-gain-loss-content"
                  amount={record?.bonds.capital_gain_loss?.amount_nett}
                />
              </Flex>
            </Collapsible>
          </BorderedContainer>
        </If>
      </DetailModal>

      <HistoryFeeModal
        visible={
          visible && Boolean(record) && modalType === HistoryModalTypeProps.FEE
        }
        onCancel={() => setModalType(HistoryModalTypeProps.DETAIL)}
      />
    </>
  );
};

export default DetailModal;
